import React from 'react'
import { action } from '@storybook/addon-actions'
import { storiesOf } from '@storybook/react'
import { withKnobs, boolean } from '@storybook/addon-knobs/react'

import GamesScreen from 'routes/Games/GamesScreen'

import {
  UserInfoResponse,
  User,
  Money,
  GameDetailsResponse,
  EditInvitationResponse,
  GamesResponse,
  Game,
  Match,
  MatchStatus,
  LeaderboardEntry,
  Team,
  SponsoredMatch,
  Jersey,
  UserRelationship,
  GameHistoryResponse,
  GeneralResponse,
  ArchiveGame,
} from 'protos'
import { MemoryRouter } from 'react-router'
import { MessageHandler } from 'types'
import noop from 'lodash-es/noop'
import Chance from 'chance'

import { fakeApiCall } from './utils'

// fake data
const chance = new Chance(7)

const fakeUserInfo = UserInfoResponse.fromObject({
  user: User.fromObject({
    balance: [
      Money.fromObject({ value: 10000 })
    ],
  }),
})

const randomTeam = (opts?: object) => Team.fromObject({
  teamId: chance.guid(),
  name: 'Liverpool FC',
  jersey: Jersey.fromObject({
    baseColor: chance.color().substr(1),
    sleeveColor: chance.color().substr(1),
    numberColor: chance.color().substr(1),
  }),
  ...opts
})

const randomMatch = (opts?: object) => Match.fromObject({
  matchId: chance.guid(),
  homeTeam: randomTeam(),
  awayTeam: randomTeam(),
  status: MatchStatus.MS_UPCOMING,
  ...opts
})

const randomLeaderboardEntry = (opts?: object) => LeaderboardEntry.fromObject({
  team: [],
  ...opts
})

const randomGame = (opts?: object) => Game.fromObject({
  gameId: chance.guid(),
  match: randomMatch(),
  ownEntry: randomLeaderboardEntry(),
  maxPot: Money.fromObject({ value: 10000 }),
  entryFee: Money.fromObject({ value: 100 }),
  ...opts
})

const randomUser = (options?: Object) =>
  User.fromObject({
    userId: chance.guid(),
    name: chance.name(),
    photoUrl: `http://i.pravatar.cc/128?img=${chance.integer({ min: 1, max: 70 })}`,
    relationship: UserRelationship.UR_NONE,
    ...options,
  })

const fakeGameDetails = GameDetailsResponse.fromObject({
  game: randomGame({
    host: randomUser(),
  }),
})

const randomArchiveGame = (options?: Object) =>
  ArchiveGame.fromObject({
    game: randomGame(),
    ...options,
  })

const fakeGameHistory = (hasAckedGames: boolean, hasUnackedGames: boolean) => GameHistoryResponse.fromObject({
  game: hasAckedGames ? [
    randomArchiveGame(),
    randomArchiveGame(),
    randomArchiveGame(),
    randomArchiveGame(),
  ] : [],
  unackedGame: hasUnackedGames ? [
    randomArchiveGame(),
    randomArchiveGame(),
  ]
    : [],
})

const fakeStream = <T extends object>(message: T, errorCode?: string) =>
  (
    onmessage: MessageHandler<T>,
    onerror: MessageHandler<Error>,
  ) => {
    if (errorCode) {
      onerror(new Error(errorCode))
    } else {
      onmessage(message)
    }
    return noop
  }

const fakeDeclineInvitation = EditInvitationResponse.fromObject({
  result: EditInvitationResponse.Result.EIR_SUCCESS,
})

// stories

storiesOf('GamesScreen', module)
  .addDecorator(withKnobs)
  .add('default', () => {
    const hasActiveGames = boolean('hasActiveGames', false)
    const hasFeaturedMatch = boolean('hasFeaturedMatch', false)
    const hasAckedGames = boolean('hasAckedGames', false)
    const hasUnackedGames = boolean('hasUnackedGames', false)
    const badServer = boolean('badServer', false)

    const featuredMatch = SponsoredMatch.fromObject({
      match: randomMatch(),
    })

    const gameList = hasActiveGames
      ? [
        randomGame({ invitation: true }),
        randomGame(),
        randomGame(),
        randomGame(),
      ]
      : []

    const gameHistory = fakeGameHistory(hasAckedGames, hasUnackedGames)

    const activeGameList = GamesResponse.fromObject({
      game: gameList,
      sponsored: hasFeaturedMatch ? featuredMatch : undefined,
      unackedGames: gameHistory.unackedGame.length,
    })

    const errorCode = badServer ? 'Could not retrieve data from the server.' : undefined

    return (
      <MemoryRouter initialEntries={['/games']}>
        <GamesScreen
          key={
            Number(hasActiveGames)
            + 2 * Number(hasFeaturedMatch)
            + 4 * Number(hasAckedGames)
            + 8 * Number(hasUnackedGames)
            + 16 * Number(badServer)
          }
          getActiveGameList={() => fakeApiCall(fakeStream(activeGameList, errorCode), 500)}
          getGameHistory={() => fakeApiCall(gameHistory, 500, errorCode)}
          declineInvitation={() => fakeApiCall(fakeDeclineInvitation, 500, errorCode)}
          getGameDetails={(gameId: string) => fakeApiCall(fakeGameDetails, 500, errorCode)}
          acknowlegdeAllGameResult={
            () => fakeApiCall(GeneralResponse.fromObject({ result: GeneralResponse.Result.GR_SUCCESS }))}
          getUrlForGameDetails={() => '/games'}
          userInfo={fakeUserInfo}
          isNewUser={false}
          makeUserOld={noop}
          acknowledgeQuizIntro={(...args) => Promise.resolve(action('Acknowledge Quiz Intro')(...args))}
          saveQuizAnswer={(...args) => Promise.resolve(action('Save Quiz Answer')(...args))}
        />
      </MemoryRouter>
    )
  })
